'use client';

import { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
    CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Copy,
    Check,
    Upload,
    Sparkles,
    Loader2,
    RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { gostHashContent } from '../../data/gost-hash-content';
import { cn } from '@/lib/utils';

// GOST crypto library
const gostCrypto = require('gost-crypto');

const sampleText =
    'Hello, world! This is a sample text for GOST hashing.';

type TabValue = 'text' | 'file';

interface FileInfo {
    name: string;
    size: number;
    type: string;
}

export default function GOSTHashTool() {
    const [input, setInput] = useState('');
    const [hash, setHash] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<TabValue>('text');
    const [file, setFile] = useState<File | null>(null);
    const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const { toast } = useToast();

    const generateGOSTHash = (data: string) => {
        try {
            const encoder = new TextEncoder();
            const dataBuffer = encoder.encode(data);
            const hashBuffer = gostCrypto.gostDigest(
                gostCrypto.algorithms.GOST_R_34_11_256,
                dataBuffer
            );
            return Array.from(new Uint8Array(hashBuffer))
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');
        } catch (err) {
            console.error(err);
            throw new Error('Failed to generate GOST hash');
        }
    };

    // Handlers
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        setInput(e.target.value);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (file: File) => {
        setFile(file);
        setFileInfo({
            name: file.name,
            size: file.size,
            type: file.type || 'Unknown',
        });
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleUseSample = () => {
        setInput(sampleText);
        setFile(null);
        setFileInfo(null);
    };

    const resetInput = () => {
        setInput('');
        setFile(null);
        setFileInfo(null);
        setHash('');
    };

    const copyToClipboard = () => {
        if (!hash) return;
        navigator.clipboard.writeText(hash).then(() => {
            setIsCopied(true);
            toast({ title: 'Copied!', description: 'Hash copied to clipboard' });
            setTimeout(() => setIsCopied(false), 2000);
        });
    };

    useEffect(() => {
        if (!input) {
            setHash('');
            return;
        }
        setIsLoading(true);
        const timer = setTimeout(() => {
            try {
                const generatedHash = generateGOSTHash(input);
                setHash(generatedHash);
            } catch {
                toast({
                    title: 'Error',
                    description: 'Failed to generate hash. Try again.',
                    variant: 'destructive',
                });
            } finally {
                setIsLoading(false);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [input]);

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <ToolPageIntro title="GOST Hash Generator">
                Generate GOST hash from text or files. Russian cryptographic standard. Paste or upload for instant results.
            </ToolPageIntro>

                <Card>
                    <CardHeader>
                        <CardTitle>Input</CardTitle>
                        <CardDescription>
                            Enter your text or upload a file to generate its GOST hash.
                        </CardDescription>
                    </CardHeader>

                    <CardContent>
                        <Tabs
                            defaultValue="text"
                            onValueChange={(v) => setActiveTab(v as TabValue)}
                        >
                            <TabsList className="grid w-full grid-cols-2 mb-4">
                                <TabsTrigger value="text">Text</TabsTrigger>
                                <TabsTrigger value="file">File</TabsTrigger>
                            </TabsList>

                            {/* Text Tab */}
                            <TabsContent value="text">
                                <Textarea
                                    placeholder="Enter text to generate GOST hash..."
                                    className="min-h-[200px]"
                                    value={input}
                                    onChange={handleInputChange}
                                />
                                <div className="flex justify-between mt-2">
                                    <div className="text-sm text-muted-foreground">
                                        {input.length} characters
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleUseSample}
                                        disabled={isLoading}
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" /> Use Sample Text
                                    </Button>
                                </div>
                            </TabsContent>

                            {/* File Tab */}
                            <TabsContent value="file">
                                <div
                                    className={cn(
                                        'relative border-2 border-dashed p-8 rounded-lg text-center transition-colors',
                                        dragActive ? 'border-primary bg-primary/10' : 'border-border',
                                        fileInfo ? 'border-green-500' : ''
                                    )}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <div className="flex flex-col items-center justify-center space-y-2">
                                        <Upload className="h-12 w-12 text-muted-foreground" />
                                        {fileInfo && (
                                            <div className="text-sm text-muted-foreground">
                                                {fileInfo.name} ({fileInfo.size} bytes)
                                            </div>
                                        )}
                                    </div>

                                    <pre className="bg-muted/50 p-4 rounded-md overflow-x-auto text-sm font-mono mt-4">
                                        {isLoading ? (
                                            <div className="flex items-center space-x-2">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>Generating hash...</span>
                                            </div>
                                        ) : hash ? (
                                            hash
                                        ) : (
                                            <span className="text-muted-foreground">
                                                Upload a file to generate its hash.
                                            </span>
                                        )}
                                    </pre>

                                    {hash && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute top-2 right-2 h-8 w-8"
                                            onClick={copyToClipboard}
                                            disabled={isLoading}
                                        >
                                            {isCopied ? (
                                                <Check className="h-4 w-4 text-green-500" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                            <span className="sr-only">Copy hash</span>
                                        </Button>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </CardContent>

                    <CardFooter className="flex justify-between">
                        <div className="text-sm text-muted-foreground">
                            {hash ? `Hash length: ${hash.length} characters` : ''}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={resetInput}
                            disabled={!input && !file}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" /> Reset
                        </Button>
                    </CardFooter>
                </Card>

                {/* Informational Content */}
                <div className="mt-16">
                    <InformationalContent
                        title={gostHashContent.title}
                        description={gostHashContent.description}
                        content={gostHashContent.content}
                        faqs={gostHashContent.faqs}
                        relatedTools={gostHashContent.relatedTools}
                    />
                </div>
            </div>

            <Toaster />
        </div>
    );
}